import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { el, mount } from "src/app/support/dom";
import { sntTranslate } from "src/app/support/translate";
import { InstallationBackend } from "src/app/backend/api/installation.backend";
import { IntegrationBackend } from "src/app/backend/api/integration.backend";
import { defer, forkJoin, iif, lastValueFrom, map, of, switchMap } from "rxjs";
import {
  ConnectIntegrationConnectionResult,
  ConnectIntegrationConnectionResultType,
  ConnectIntegrationInfo
} from "src/app/modules/bootstrap/functions/integrations/connect-integration/connect-integration.connector";
import { Connection } from "src/app/backend/models/connection";

@Injectable()
export class ConnectIntegrationOAuthConnector implements ConnectIntegrationOAuthConnector {
  private readonly integrationBackend = inject(IntegrationBackend);
  private readonly installationBackend = inject(InstallationBackend);

  connect({
    integration,
    installation,
    isRefresh,
    refreshConnectionId,
    phase
  }: ConnectIntegrationInfo): Promise<ConnectIntegrationConnectionResult> {
    return new Promise<ConnectIntegrationConnectionResult>(async (res) => {
      const authorizationUrl = new URL(
        await lastValueFrom(
          this.integrationBackend.loadAuthorizationUrl(integration.id)
        )
      );

      const authorizeResult = await this.authorizeForCodeAndState({
        integrationName: integration.name,
        authorizationUrl: authorizationUrl.toString()
      });

      if (!authorizeResult) {
        res({
          result: ConnectIntegrationConnectionResultType.CANCELED
        });
        return;
      }

      if (!installation) {
        res({
          result: ConnectIntegrationConnectionResultType.CANCELED
        });
        return;
      }

      const credentials = await lastValueFrom(
        this.installationBackend.redeem({
          installationId: installation.id,
          code: authorizeResult.code,
          state: authorizeResult.state
        })
      );

      const connection = await lastValueFrom(
        forkJoin({
          createdConnection: iif(
            () => !isRefresh,
            defer(() => {
              return this.installationBackend.createConnection({
                provider: integration.provider,
                type: "oauth2",
                credentials: {
                  accessToken: credentials.accessToken,
                  expiresAt: credentials.expiresAt,
                  refreshToken: credentials.refreshToken
                },
                default: phase === "install",
                installationId: installation.id
              });
            }),
            of(undefined)
          ),
          updatedConnection: iif(
            () => !!isRefresh,
            defer(() => {
              return this.installationBackend
                .loadConnectionById(refreshConnectionId!)
                .pipe(
                  switchMap((connection: Connection) => {
                    return this.installationBackend
                      .updateConnectionCredentials({
                        connectionId: refreshConnectionId!,
                        version: connection.version,
                        type: "oauth2",
                        credentials: {
                          accessToken: credentials.accessToken,
                          expiresAt: credentials.expiresAt,
                          refreshToken: credentials.refreshToken
                        }
                      })
                      .pipe(
                        switchMap(() =>
                          this.installationBackend.loadConnectionById(
                            refreshConnectionId!
                          )
                        )
                      );
                  })
                );
            }),
            of(undefined)
          )
        }).pipe(
          map(({ createdConnection, updatedConnection }) => {
            return updatedConnection || createdConnection;
          })
        )
      );

      res({
        result: ConnectIntegrationConnectionResultType.SUCCEED,
        connection
      });
    });
  }

  private authorizeForCodeAndState({
    integrationName,
    authorizationUrl
  }: {
    integrationName: string;
    authorizationUrl: string;
  }): Promise<{ code: string; state: string } | null> {
    return new Promise<{ code: string; state: string } | null>(async (res) => {
      let resolved = false;

      const broadcastChannel = new BroadcastChannel("oauth-callback");
      broadcastChannel.onmessage = (broadcastMessage: MessageEvent) => {
        switch (broadcastMessage.data?.type) {
          case "completed": {
            resolved = true;
            res({
              code: broadcastMessage.data?.values?.code,
              state: broadcastMessage.data?.values?.state
            });

            break;
          }
        }
      };

      this.launchPopup({
        integrationName: integrationName,
        authorizationUrl: authorizationUrl.toString()
      }).then(({ result, done }) => {
        done();

        if (!resolved) {
          res(null);
        }
      });
    });
  }

  private launchPopup({
    integrationName,
    authorizationUrl
  }: {
    integrationName: string;
    authorizationUrl: string;
  }): Promise<{
    result: ConnectIntegrationConnectionResult;
    done: () => void;
  }> {
    return new Promise<{
      result: ConnectIntegrationConnectionResult;
      done: () => void;
    }>((res) => {
      const wall = el(
        "div",
        {
          class:
            "absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/50"
        },
        [
          el("div", [
            el(
              "span",
              { class: "text-xl text-slate-50" },
              sntTranslate(
                marker("Prosegui nella finestra di autorizzazione...")
              )
            )
          ]),
          el("div", [
            el(
              "span",
              {
                class:
                  "text-sm text-slate-50 underline select-none cursor-pointer"
              },
              sntTranslate(marker("Clicca per annullare")),
              (spanElement: HTMLElement) => {
                spanElement.addEventListener(
                  "click",
                  () => {
                    clearTimeout(timeoutId);

                    res({
                      result: {
                        result: ConnectIntegrationConnectionResultType.CANCELED
                      },
                      done: () => {
                        wall.remove();
                      }
                    });
                  },
                  { once: true }
                );
              }
            )
          ])
        ]
      );

      const broadcastChannel = new BroadcastChannel("oauth-callback");
      broadcastChannel.onmessage = (broadcastMessage: MessageEvent) => {
        switch (broadcastMessage.data?.type) {
          case "completed": {
            wall.remove();
            break;
          }
        }
      };

      window.open(authorizationUrl, integrationName, "width=1024,height=800");

      const timeoutId = setTimeout(() => {
        res({
          result: {
            result: ConnectIntegrationConnectionResultType.CANCELED
          },
          done: () => {
            wall.remove();
          }
        });
      }, 120_000);

      mount(document.body, wall);
    });
  }
}
